import React, { useState, useRef, useEffect } from "react";
import Markdown from "react-markdown";
import { ActionIcon } from "@mantine/core";
import { LabeledTextInput } from "./TextInput";
import TypingAnimation from "./TypingAnimation";
import { ButtonWithIcon } from "./ButtonWithIcon";
import ButtonWithPopoverFeedback from "./ButtonWithPopoverFeedback";
import {
	IconArrowUp,
	IconArrowDown,
	IconCopy,
	IconPlayerStopFilled,
} from "@tabler/icons-react";
import { aiSourceName } from "./ChatInterface/ConversationIcon";
import "./ConversationInterface.css";

const SCROLL_BOTTOM_THRESHOLD = 64;
const SCROLL_SUPPRESSION_MS = 600;

const ConversationInterface = ({
	messages,
	submitQuestion,
	IconComponent,
	endOfMessagesComponent,
	userTextInputAllowed,
	awaitingResponse = false,
	onStop,
	showUnsavedDisclaimer = true,
	hideMistakesDisclaimer = false,
	messageActions,
	scrollAreaOverlay,
	bubbleRightMargin = 8,
	inputAccessory,
}) => {
	const [userInput, setUserInput] = useState("");
	const [currentlyTyping, setCurrentlyTyping] = useState(false);
	const [showScrollToBottom, setShowScrollToBottom] = useState(false);
	// the `animateTyping` flag on a message is set once when the reply is
	// created and never flipped back off, so it can't be used on its own to
	// tell whether typing has actually finished — track completion locally
	const [finishedTypingIndices, setFinishedTypingIndices] = useState(
		() => new Set()
	);

	const messagesEndRef = useRef(null);
	const textInputFormRef = useRef(null);
	const scrollContainerRef = useRef(null);
	const isAtBottomRef = useRef(true);
	const suppressScrollRef = useRef(false);
	const suppressTimeoutRef = useRef(null);

	const submitUserQuestion = () => {
		submitQuestion(userInput);
		textInputFormRef.current.textInput.value = "";
		setUserInput("");
		jumpToBottom();
	};
	// "instant" is used while auto-following live-streaming content (typing
	// ticks) so the scroll position never lags behind and flickers the
	// "scroll to bottom" button; "smooth" is reserved for one-off jumps
	const scrollToBottom = (behavior = "instant") => {
		messagesEndRef.current.scrollIntoView({ behavior });
	};
	// forces a scroll-to-bottom (new message sent, or the user clicked the
	// "scroll to bottom" button). Jumps instantly regardless of distance —
	// a "smooth" scroll here takes longer the further up the user is, which
	// let scroll events land mid-animation and flicker the button back on.
	const jumpToBottom = () => {
		isAtBottomRef.current = true;
		setShowScrollToBottom(false);
		suppressScrollRef.current = true;
		clearTimeout(suppressTimeoutRef.current);
		suppressTimeoutRef.current = setTimeout(() => {
			suppressScrollRef.current = false;
		}, SCROLL_SUPPRESSION_MS);
		scrollToBottom("instant");
	};

	useEffect(() => {
		if (isAtBottomRef.current) scrollToBottom("smooth");
	}, [messages]);

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return undefined;
		const handleScroll = () => {
			if (suppressScrollRef.current) return;
			const atBottom =
				el.scrollHeight - el.scrollTop - el.clientHeight <=
				SCROLL_BOTTOM_THRESHOLD;
			isAtBottomRef.current = atBottom;
			setShowScrollToBottom(!atBottom);
		};
		el.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			el.removeEventListener("scroll", handleScroll);
			clearTimeout(suppressTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		if (userTextInputAllowed) {
			textInputFormRef.current?.textInput?.focus();
		}
	}, [userTextInputAllowed]);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				flexGrow: "1",
				overflow: "hidden",
			}}
		>
			<div
				ref={scrollContainerRef}
				style={{
					display: "flex",
					flexDirection: "column",
					flex: "1 1 auto",
					overflow: "auto",
					gap: 16,
					paddingBottom: 32,
				}}
			>
				{scrollAreaOverlay}
				{messages.map(
					(
						{ source, content, animateTyping, timestamp, thinking },
						index
					) => {
						const isAiMessage = source === aiSourceName;
						const stillTyping =
							animateTyping && !finishedTypingIndices.has(index);
						return (
							<div
								key={index}
								className="ConversationMessageRow"
								style={{
									marginTop: 8,
									marginBottom: 8,
									marginLeft: 8,
									marginRight: bubbleRightMargin,
									display: "flex",
									gap: 8,
								}}
							>
								<div style={{ width: 32 }}>
									<IconComponent sourceName={source} />
								</div>
								<div style={{ minWidth: 0, flex: 1 }}>
									<div
										style={{
											display: "flex",
											alignItems: "baseline",
											gap: 8,
											lineHeight: "32px",
											height: 32,
										}}
									>
										<span
											style={{
												fontWeight: "bold",
												fontSize: 16,
											}}
										>
											{source}
										</span>
										{timestamp && (
											<span
												style={{
													fontSize: 12,
													color: "#999",
												}}
											>
												{timestamp}
											</span>
										)}
										{messageActions?.(
											{
												source,
												content,
												animateTyping,
												timestamp,
											},
											index
										)}
									</div>
									<div
										style={{
											fontSize: 14,
											background: "#eeeeee",
											overflowWrap: "break-word",
											lineHeight: "1.75em",
											padding: isAiMessage
												? "1px 16px"
												: "16px",
											borderRadius: 8,
											position: "relative",
										}}
									>
										{animateTyping ? (
											<TypingAnimation
												fullText={content}
												onType={() => {
													if (isAtBottomRef.current)
														scrollToBottom();
												}}
												startTyping={() => {
													setCurrentlyTyping(true);
												}}
												finishedTyping={() => {
													setCurrentlyTyping(false);
													setFinishedTypingIndices(
														(prev) => {
															const next =
																new Set(prev);
															next.add(index);
															return next;
														}
													);
													if (isAtBottomRef.current)
														scrollToBottom();
												}}
											/>
										) : isAiMessage ? (
											<Markdown>{content}</Markdown>
										) : (
											<span>{content}</span>
										)}
										{!stillTyping && !thinking ? (
											<div className="ConversationMessageActions">
												<ButtonWithPopoverFeedback
													iconComponent={
														<IconCopy
															size={16}
															style={{
																marginTop: 2,
															}}
														/>
													}
													onClick={() =>
														navigator.clipboard.writeText(
															content
														)
													}
													feedbackMessage="Copied to clipboard!"
													duration={700}
													title="Copy to clipboard"
												/>
											</div>
										) : null}
									</div>
								</div>
							</div>
						);
					}
				)}
				{(!currentlyTyping && endOfMessagesComponent) || null}
				{<div ref={messagesEndRef} key="endOfMessages" />}
				{showScrollToBottom ? (
					<div
						style={{
							position: "sticky",
							bottom: 8,
							height: 0,
							zIndex: 3,
						}}
					>
						<div
							style={{
								position: "absolute",
								right: 12,
								bottom: 0,
							}}
						>
							<ActionIcon
								variant="filled"
								radius="xl"
								size="lg"
								onClick={jumpToBottom}
								aria-label="Scroll to bottom"
								style={{
									boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
								}}
							>
								<IconArrowDown size={18} />
							</ActionIcon>
						</div>
					</div>
				) : null}
			</div>
			<div style={{ flexShrink: 0, marginTop: 4, position: "relative" }}>
				{inputAccessory ? (
					<div
						style={{
							position: "absolute",
							bottom: "100%",
							left: 0,
							right: 0,
						}}
					>
						{inputAccessory}
					</div>
				) : null}
				<div
					style={{ display: "flex", gap: 16, alignItems: "stretch" }}
				>
					<div style={{ flexGrow: 1 }}>
						<LabeledTextInput
							formRef={textInputFormRef}
							onChange={(key, val) => {
								setUserInput(val);
							}}
							onEnterKey={(e) => {
								submitUserQuestion();
								e.preventDefault();
								return false;
							}}
							disabled={!userTextInputAllowed}
							placeholder={"Message LexWorkplace AI..."}
							autocomplete="one-time-code"
						/>
					</div>
					<div style={{ marginLeft: "auto" }}>
						{awaitingResponse && onStop ? (
							<ButtonWithIcon
								handler={onStop}
								buttonText="Stop"
								iconComponent={
									<IconPlayerStopFilled
										size={16}
										style={{ verticalAlign: "sub" }}
									/>
								}
							/>
						) : (
							<ButtonWithIcon
								handler={() => submitUserQuestion()}
								disabled={
									userInput.trim() === "" ||
									!userTextInputAllowed
								}
								buttonText="Send"
								iconComponent={
									<IconArrowUp
										size={16}
										style={{ verticalAlign: "sub" }}
									/>
								}
							/>
						)}
					</div>
				</div>
				{showUnsavedDisclaimer || !hideMistakesDisclaimer ? (
					<div
						style={{
							fontFamily: "MainFont-Light",
							fontSize: 12,
							lineHeight: 1.4,
						}}
					>
						{showUnsavedDisclaimer ? (
							<div>
								This AI conversation will not be saved unless
								you explicitly save it as a Matter Note.
							</div>
						) : null}
						{hideMistakesDisclaimer ? null : (
							<div>
								LexWorkplace AI can make mistakes. Consider
								checking important information.
							</div>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ConversationInterface;
